import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { GoalForm } from './GoalForm';
import { useCreateGoal } from '@/shared/hooks';
import { defaultValues, type CreateGoalFormData } from './types/goals.types';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

interface CreateGoalModalProps {
  parentGoalId?: string;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const CreateGoalModal: React.FC<CreateGoalModalProps> = ({
  parentGoalId,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const { t } = useTranslation('goals');
  const [internalOpen, setInternalOpen] = useState(false);
  const createGoalMutation = useCreateGoal();
  const currentEmployee = useEmployeeContext();

  // Use controlled or internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const initialValues = useMemo<CreateGoalFormData>(() => {
    return {
      ...defaultValues,
      creator: currentEmployee._id,
      ...(parentGoalId && { parent: parentGoalId }),
    };
  }, [parentGoalId, currentEmployee._id]);

  const handleSubmit = async (formData: CreateGoalFormData) => {
    const { description, ...goalData } = formData;
    await createGoalMutation.mutateAsync({
      ...goalData,
      ...(description?.trim() ? { description } : {}),
      assignees: goalData.assignees ?? [],
    });

    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const defaultTrigger = (
    <Button
      size="sm"
      className="shadow-sm hover:shadow-md transition-shadow rtl:flex-row-reverse"
    >
      <Plus className="h-4 w-4 me-2" />
      {t('createGoalButton')}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      {!trigger && <DialogTrigger asChild>{defaultTrigger}</DialogTrigger>}
      <DialogContent
        size="lg"
        className="flex flex-col max-h-[90vh] p-0"
        dismissibleByOutsideClick={false}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-xl font-bold">
            {parentGoalId ? t('createChildGoalTitle') : t('createGoalTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 px-6 py-4">
          <GoalForm
            initialValues={initialValues}
            isPending={createGoalMutation.isPending}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel={t('createGoal')}
            submittingLabel={t('creating')}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
